import Checkbox from '@mui/material/Checkbox';
import Tab from '@mui/material/Tab';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import Tabs from '@mui/material/Tabs';
import { Label } from '@trucktrax/trucktrax-common';
import cx from 'classnames';
import React, { useMemo, useState } from 'react';

import { PlantOption } from '../../../services/emailSubscriptionsService';
import { EmailNotificationType } from '../../../types';
import { noop } from '../../../util/appUtil';
import {
  cellKey,
  NOTIFICATION_TYPE_OPTIONS,
  NotificationCell,
} from '../../../util/emailNotificationUtil';
import styles from './NotificationsField.module.css';

export const NO_PLANTS_MESSAGE = 'No plants are available.';

/** One region's block in the list: the heading, and the plants under it. */
interface PlantRegion {
  url: string;
  name: string;
  plants: PlantOption[];
}

/**
 * Which plants a recipient gets each notification for.
 *
 * A tab per notification topic, and under it every plant the recipient can be
 * subscribed against, grouped under its region with a checkbox on the right. Ticking
 * one is one stored row: the API keys a subscription on (email, type, region, plant),
 * and a tick names the topic and the plant, with the region travelling along because a
 * plant belongs to one.
 *
 * Topics are the tabs rather than columns because an admin sets up one notification at
 * a time -- "who gets Diversions, and for which plants" is the question being answered,
 * and answering it for every plant at once is what the region groups are for. Regions
 * are headings rather than tabs so the whole answer for a topic is on one screen and
 * scannable, with no tab hiding half of it.
 *
 * The selection is held by the caller as one flat list of cell keys spanning every
 * topic and region, so switching tabs cannot lose an edit.
 *
 * Each region heading carries a toggle for its own plants, on this topic only. It is
 * scoped to the region it heads rather than to everything on screen: setting one
 * region is the routine gesture, and a control that quietly reached across regions
 * would be a different action than the one it appears to offer.
 */
export default function NotificationsField(props: NotificationsFieldProps) {
  const {
    id,
    value = [],
    plantOptions = [],
    regionNames = {},
    allowedRegionUrls,
    label,
    isRequired = false,
    disabled = false,
    errorMessage,
    className,
    dataTest,
    onChange = noop,
  } = props;

  const selected = useMemo(() => new Set(value), [value]);
  const [topicIndex, setTopicIndex] = useState(0);
  const topic = NOTIFICATION_TYPE_OPTIONS[topicIndex] ?? NOTIFICATION_TYPE_OPTIONS[0];

  /**
   * Regions that have plants, name-ordered, each with its plants name-ordered.
   *
   * Built from the plant list rather than the region list, so a region with no plants
   * cannot produce an empty heading. Narrowed to `allowedRegionUrls` when the caller
   * supplies one -- the regions the chosen recipient belongs to.
   */
  const regions = useMemo<PlantRegion[]>(() => {
    const allowed = allowedRegionUrls ? new Set(allowedRegionUrls) : undefined;
    const byRegion = new Map<string, PlantOption[]>();

    plantOptions.forEach(plant => {
      const url = plant.region.url ?? '';
      if (url === '' || (allowed && !allowed.has(url))) {
        return;
      }
      if (!byRegion.has(url)) {
        byRegion.set(url, []);
      }
      byRegion.get(url)!.push(plant);
    });

    return Array.from(byRegion.entries())
      .map(([url, plants]) => ({
        url,
        name: regionNames[url] ?? url,
        plants: [...plants].sort((a, b) => a.name.localeCompare(b.name)),
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [plantOptions, regionNames, allowedRegionUrls]);

  // Guarded like EmailRecipientField: AdminAddModal spreads the field config, so `id`
  // is only ever present because the config supplies it.
  const emit = (keys: Set<string>) => {
    if (id) {
      onChange({ [id]: Array.from(keys) });
    }
  };

  const toggle = (keys: string[], on: boolean) => {
    const next = new Set(selected);
    keys.forEach(key => (on ? next.add(key) : next.delete(key)));
    emit(next);
  };

  const keysFor = (plants: PlantOption[]) => plants.map(plant => cellKey(topic.key, plant.key));

  /** All ticked / some ticked, for a toggle covering `keys`. */
  const stateOf = (keys: string[]) => {
    const ticked = keys.filter(key => selected.has(key)).length;
    return { all: keys.length > 0 && ticked === keys.length, some: ticked > 0 };
  };

  /**
   * The whole row is the target, not just the box: rows are wide and the box is small,
   * and a list this long is quicker to work through when anywhere in the row will do.
   *
   * The checkbox therefore carries no `onChange` of its own -- MUI's own selection
   * pattern. A click on the box bubbles to the row and toggles once; handling it in
   * both places would toggle twice and net to nothing. The box stays a real focusable
   * checkbox, so Space on it still fires a click that reaches the row.
   */
  const rowToggle = (keys: string[]) => () => {
    if (disabled || keys.length === 0) {
      return;
    }
    toggle(keys, !stateOf(keys).all);
  };

  const renderCheckbox = (
    keys: string[],
    ariaLabel: string,
    checkboxDataTest: string,
    indeterminate = false,
  ) => {
    const { all, some } = stateOf(keys);
    return (
      <Checkbox
        color="primary"
        checked={all}
        indeterminate={indeterminate && some && !all}
        disabled={disabled || keys.length === 0}
        className={styles.checkbox}
        inputProps={
          {
            'aria-label': ariaLabel,
            'data-test': checkboxDataTest,
          } as React.InputHTMLAttributes<HTMLInputElement>
        }
      />
    );
  };

  const renderRegion = (region: PlantRegion) => [
    <TableRow
      key={`region-${region.url}`}
      className={cx(styles.regionRow, !disabled && styles.clickableRow)}
      hover={!disabled}
      onClick={rowToggle(keysFor(region.plants))}
    >
      <TableCell className={styles.regionCell} data-test={`${dataTest}-region`}>
        {region.name}
      </TableCell>
      {/*
        align="right" rather than a text-align of ours: MUI's TableCell root sets
        `text-align: left` and emotion injects it after this app's stylesheet, so a
        same-specificity rule loses and the checkbox drifts to the left edge.
      */}
      <TableCell className={styles.checkboxCell} align="right">
        {renderCheckbox(
          keysFor(region.plants),
          `${topic.label} for every plant in ${region.name}`,
          `${dataTest}-region-toggle`,
          true,
        )}
      </TableCell>
    </TableRow>,
    ...region.plants.map(plant => (
      <TableRow
        key={plant.key}
        className={cx(!disabled && styles.clickableRow)}
        hover={!disabled}
        onClick={rowToggle([cellKey(topic.key, plant.key)])}
      >
        <TableCell className={styles.plantCell} data-test={`${dataTest}-plant`}>
          {plant.name}
        </TableCell>
        <TableCell className={styles.checkboxCell} align="right">
          {renderCheckbox(
            [cellKey(topic.key, plant.key)],
            `${topic.label} for ${plant.name}`,
            `${dataTest}-plant-toggle`,
          )}
        </TableCell>
      </TableRow>
    )),
  ];

  return (
    <div className={className}>
      {label ? <Label isRequired={isRequired}>{label}</Label> : null}
      <div className={cx(styles.frame, errorMessage && styles.hasError)} data-test={dataTest}>
        <Tabs
          value={topicIndex}
          onChange={(_event, next: number) => setTopicIndex(next)}
          /*
           * Not scrollable. There are two topics and there will not be many more, so
           * nothing can scroll -- and `variant="scrollable"` brings MUI's resize
           * observation and scroll buttons, which is machinery to no purpose inside a
           * dialog. It was copied from the region tabs this replaced, which could have
           * had a dozen entries.
           */
          className={styles.tabs}
        >
          {NOTIFICATION_TYPE_OPTIONS.map(option => (
            <Tab key={option.key} label={option.label} data-test={`${dataTest}-topic-tab`} />
          ))}
        </Tabs>
        {regions.length === 0 ? (
          <div className={styles.empty} data-test={`${dataTest}-empty`}>
            {NO_PLANTS_MESSAGE}
          </div>
        ) : (
          <div className={styles.listScroll}>
            <Table className={styles.list} size="small">
              <TableBody>{regions.flatMap(renderRegion)}</TableBody>
            </Table>
          </div>
        )}
      </div>
      {errorMessage ? (
        <div className={styles.errorMessage} data-test={`${dataTest}-error`}>
          {errorMessage}
        </div>
      ) : null}
    </div>
  );
}

export interface NotificationsFieldProps {
  /**
   * The field key. Changes are reported as `{ [id]: cellKeys }`, per the modal
   * contract. Optional only because AdminConfig types `component` as
   * `ComponentType<{}>`, which a component with a required prop cannot satisfy --
   * EmailRecipientField declares its own `id` the same way for the same reason.
   */
  id?: string;
  /** Ticked cells, as `cellKey` values, across every topic and region. */
  value?: string[];
  /** Every plant the recipient can be subscribed against, each carrying its region. */
  plantOptions?: PlantOption[];
  /** Region url to display name, for the group headings. */
  regionNames?: { [url: string]: string };
  /**
   * Limits the plants to these regions. Omit to offer every region that has plants.
   * See the caller for how a recipient's regions are resolved, and why the regions an
   * address is already subscribed in are always included.
   */
  allowedRegionUrls?: string[];
  label?: string;
  isRequired?: boolean;
  disabled?: boolean;
  errorMessage?: string;
  className?: string;
  dataTest?: string;
  onChange?: (change: { [key: string]: string[] }) => void;
}

/** Every (topic, plant) pair the field can offer, which a save maps ticks back through. */
export const notificationCells = (plantOptions: PlantOption[]): NotificationCell[] =>
  plantOptions.flatMap(plant =>
    NOTIFICATION_TYPE_OPTIONS.map(option => ({
      notificationType: option.key as EmailNotificationType,
      plantUrl: plant.key,
      regionUrl: plant.region.url ?? '',
    })),
  );
